import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { mergeGuestCart } from "@/lib/guestCart";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null while loading
  const [status, setStatus] = useState("loading"); // loading | authenticated | anonymous

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data?.data?.user || null);
      setStatus(data?.data?.user ? "authenticated" : "anonymous");
    } catch (e) {
      setUser(null);
      setStatus("anonymous");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me: rememberMe });
      setUser(data?.data?.user || null);
      setStatus("authenticated");
      // Auto-merge guest cart (if any) — Graphy-style
      try {
        const r = await mergeGuestCart(api);
        if (r.merged > 0) {
          const parts = [`${r.merged} course${r.merged > 1 ? "s" : ""} restored`];
          if (r.already_owned) parts.push(`${r.already_owned} already purchased`);
          if (r.skipped_duplicates) parts.push(`${r.skipped_duplicates} already in cart`);
          window.dispatchEvent(new CustomEvent("cart:updated"));
          // Show inline toast so learner knows their cart is intact
          import("sonner").then(({ toast }) => toast.success(`Your cart has been restored — ${parts.join(", ")}.`));
        }
      } catch { /* silent — cart merge best-effort */ }
      return { ok: true, user: data?.data?.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data?.data?.user || null);
      setStatus("authenticated");
      // Same guest-cart merge on signup
      try {
        const r = await mergeGuestCart(api);
        if (r.merged > 0) {
          window.dispatchEvent(new CustomEvent("cart:updated"));
          import("sonner").then(({ toast }) => toast.success(`Your cart has been restored — ${r.merged} course${r.merged > 1 ? "s" : ""}.`));
        }
      } catch { /* silent */ }
      return { ok: true, user: data?.data?.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* logout best-effort */
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((u) => (u ? { ...u, ...patch } : u));
  }, []);

  const value = useMemo(
    () => ({ user, status, login, register, logout, refresh, updateUser }),
    [user, status, login, register, logout, refresh, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
